import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSocket } from "../socket/SocketProvider";
import Modal from "./Modal";
import { setToken } from "../../store/slice/vonageSlice";
import { setCallerData } from "../../store/slice/callingSlice";
import { appointmentCancel } from "../../service/videoCallService";
import { apiRequest } from "../../util/async/apiUtils";
import { sanitizeHtml } from "../../helpers/sanitizeHtml";
import {
  startRingtoneFromUserGesture,
  stopRingtone,
  subscribeRingtoneBlocked,
  isRingtonePlayBlocked,
  isIOS,
} from "../../util/ringtone";
import { stopAllCallMedia } from "../../util/stopAllCallMedia";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";
import { getCallPeer, mediaUrl, personName } from "../../util/callPeerDisplay";
import defaultUserImage from "../../assets/images/user.svg";
import PreCallDeviceCheck from "./PreCallDeviceCheck";
import {
  hasUsableFreshDeviceReadiness,
  saveCallDeviceReadiness,
} from "../../util/deviceReadiness";
import { isInstantAppointment } from "../../util/timezoneHelpers";
import { resolveConsultationPath } from "../../util/callPathHelpers";
// Language configurations
const TRANSLATIONS = {
  heading: {
    en: "want to start a <span class='text-prontopsy-blue text-xl font-bold'>{mode}</span> consult with you.",
    es: "Quiero iniciar una consultar de <span class='text-prontopsy-blue text-xl font-bold'>{mode}</span> contigo.",
    it: "voglio iniziare una consultare <span class='text-prontopsy-blue text-xl font-bold'>{mode}</span> con te.",
  },
  scheduleHeading: {
    en: "want to start a <span class='text-prontopsy-blue text-xl font-bold'>{mode}</span> consult in booked call with you.",
    es: "quiere iniciar una consulta de <span class='text-prontopsy-blue text-xl font-bold'>{mode}</span> en una llamada programada contigo.",
    it: "vuole iniziare un consulto <span class='text-prontopsy-blue text-xl font-bold'>{mode}</span> in una chiamata prenotata con te.",
  },
  subheading: {
    en: "Informed Consent has been accepted and Payment has been authorized.",
    es: "Se ha aceptado el Consentimiento Informado y se ha autorizado el Pago.",
    it: "Il Consenso Informato è stato accettato e il Pagamento è stato autorizzato.",
  },
  join: {
    en: "Join Now",
    es: "Únete Ahora",
    it: "Iscriviti Ora",
  },
  cancel: {
    en: "Cancel",
    es: "Cancelar",
    it: "Cancellare",
  },
  tapForSound: {
    en: "🔊 Tap here to enable ringtone (required on iPhone)",
    es: "🔊 Toca aquí para activar el tono (necesario en iPhone)",
    it: "🔊 Tocca qui per attivare la suoneria (necessario su iPhone)",
  },
};

export const JoinCallModal = ({
  isOpen,
  setIsOpen,
  data,
  sessionId,
  sessionToken,
  callerData,
  audioRef,
  callId,
  service,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socket = useSocket();
  const toastDisplayedRef = useRef(false);

  // Get user language from Redux or localStorage
  const reduxLang = useSelector((state) => state?.languageSlice?.data);
  const language = localStorage.getItem("language") || reduxLang;

  // Get user details and appointment data
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const storedAppointment = JSON.parse(localStorage.getItem("appointmentData"));

  const [callReciveData, setCallReciveData] = useState({});
  const [soundBlocked, setSoundBlocked] = useState(() => isRingtonePlayBlocked());
  const [showDeviceCheck, setShowDeviceCheck] = useState(false);
  const [checkingReadiness, setCheckingReadiness] = useState(false);
  const ringDeadlineRef = useRef(0);
  const ringPausedRemainingRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSoundBlocked(false);
      return undefined;
    }
    setSoundBlocked(isRingtonePlayBlocked());
    return subscribeRingtoneBlocked(setSoundBlocked);
  }, [isOpen]);

  const handleEnableSound = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // Only from the orange banner — not from Join/Cancel (that re-started ring after accept)
    if (isRingtonePlayBlocked()) {
      startRingtoneFromUserGesture().then((ok) => {
        if (ok) setSoundBlocked(false);
      });
    }
  };

  useEffect(() => {
    if (callId === data?.consultantData?._id) {
      setCallReciveData(data?.consultantData);
    } else if (callId === data?.clientData?._id) {
      setCallReciveData(data?.clientData);
    }
  }, [callId, data]);

  const appointment = storedAppointment || callerData;

  // Peer = OTHER party (consultant sees client; client sees consultant)
  const peerInfo = getCallPeer({
    roleCode: userDetails?.roleCode,
    myId: userDetails?._id || userDetails?.id,
    appointment,
    callerData,
    data,
  });
  const displayName = peerInfo.name || "Caller";
  const displayImage = peerInfo.image || defaultUserImage;

  // Keep full callerData for join/socket — never rebuild consultant from `data` (that is the peer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callData =
    appointment?.callerData ||
    callerData?.callerData ||
    {
      user: peerInfo.client
        ? {
            id: peerInfo.client.id,
            name: personName(peerInfo.client),
            image: mediaUrl(peerInfo.client.image),
            email: peerInfo.client.email,
          }
        : {
            name: `${userDetails?.firstName ?? ""} ${userDetails?.lastName ?? ""}`.trim(),
            email: userDetails?.email ?? "",
            image: mediaUrl(userDetails?.image),
            id: userDetails?._id ?? "",
          },
      consultant: peerInfo.consultant
        ? {
            id: peerInfo.consultant.id,
            name: personName(peerInfo.consultant),
            image: mediaUrl(peerInfo.consultant.image),
            email: peerInfo.consultant.email,
          }
        : {
            name: "",
            image: "",
            id: appointment?.consultantId || "",
          },
      mode: appointment?.mode || callerData?.mode,
    };

  // Get appropriate text based on current language
  const getLocalizedText = (key, params = {}) => {
    let text = TRANSLATIONS[key]?.[language] || TRANSLATIONS[key]?.en || "";

    // Replace any parameters in the text
    Object.entries(params).forEach(([param, value]) => {
      text = text.replace(`{${param}}`, value);
    });

    return text;
  };

  // const [hasStart, setHasStart] = useState(false);
  // const [isTimeOn, setIsTimeOn] = useState(false);
  // const intervalRef = useRef(null);

  // useEffect(() => {
  //   const appointmentId = appointment?.appointmentId;

  //   const fetchActiveArray = async () => {
  //     try {
  //       const res = await apiRequest("get", "appointment/get-already-in-call", {
  //         queries: { appointmentId },
  //       });
  //       const activeArray = res?.data?.data?.activeArray || [];
  //       if (activeArray.length >= 2) {
  //         setHasStart(true);
  //       } else {
  //         setHasStart(false);
  //       }
  //     } catch (err) {
  //       console.error("Polling error:", err);
  //     }
  //   };

  //   if (!isTimeOn) {
  //     // Start polling every 5 seconds
  //     intervalRef.current = setInterval(fetchActiveArray, 1000);
  //     // Run immediately as well
  //     fetchActiveArray();
  //   }

  //   // Cleanup on unmount or when call ends
  //   return () => {
  //     clearInterval(intervalRef.current);
  //   };
  // }, [appointment, isTimeOn]);

  // Handle join call action
  const handleJoinNow = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const apptId = appointment?.appointmentId || callData?.appointmentId;
    if (apptId) {
      try {
        const res = await apiRequest("get", "appointment/get-already-in-call", {
          queries: { appointmentId: apptId },
        });
        const st = String(res?.data?.data?.status || res?.data?.docs?.status || "");
        if (st === "cancel" || st === "close") {
          toast.info("This call was cancelled.", { autoClose: 4000 });
          setIsOpen(false);
          try {
            localStorage.removeItem("appointmentData");
            localStorage.removeItem("joined");
          } catch (_) {
            /* ignore */
          }
          return;
        }
      } catch (_) {
        /* if status cannot be confirmed, still try to join */
      }
    }
    // Stop this tab + all other tabs still ringing
    stopRingtone("accepted", apptId);
    try {
      if (audioRef?.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch (_) {
      /* ignore */
    }

    // Prefer full appointment callerData (has both parties) over rebuilt callData
    const joinCallerData = appointment?.callerData || callerData?.callerData || callData;
    dispatch(
      setCallerData({
        ...(appointment || callerData || {}),
        callerData: joinCallerData,
        user: joinCallerData?.user,
        consultant: joinCallerData?.consultant,
      }),
    );

    // Prefer props, then appointment payload (must have OpenTok credentials)
    const resolvedSessionId =
      sessionId || appointment?.sessionId || callerData?.sessionId;
    const resolvedToken =
      sessionToken || appointment?.token || appointment?.sessionToken || callerData?.token || callerData?.sessionToken;

    if (!resolvedSessionId || !resolvedToken) {
      toast.error("Call session is not ready yet. Please wait a moment and try Join again.", {
        autoClose: 4000,
      });
      return;
    }

    // Update storage with appointment start time + credentials
    const updatedAppointment = {
      ...appointment,
      appointmentStartTime:
        appointment?.details?.appointmentStartTime ||
        appointment?.appointmentStartTime ||
        Date.now(),
      appointmentEndTime:
        appointment?.details?.appointmentEndTime ||
        appointment?.appointmentEndTime,
      details: appointment?.details,
      sessionId: resolvedSessionId,
      token: resolvedToken,
      sessionToken: resolvedToken,
    };

    localStorage.setItem("appointmentData", JSON.stringify(updatedAppointment));

    // Set Vonage session token
    dispatch(
      setToken({
        loading: true,
        sessionId: resolvedSessionId,
        token: resolvedToken,
      }),
    );

    // if (service === "direct-access-online-psychological-counseling") {
    // Emit socket events
    const appointmentId = appointment?.appointmentId || callerData?.appointmentId;
    const callerPayload =
      appointment?.callerData ||
      callerData?.callerData ||
      callData || {
        user: {
          id: appointment?.clientId || appointment?.callerData?.user?.id,
        },
        consultant: {
          id: appointment?.consultantId || userDetails?._id,
        },
      };
    const consultantIdForJoin =
      appointment?.consultantId ||
      callerPayload?.consultant?.id ||
      userDetails?._id;
    const clientIdForJoin =
      appointment?.clientId ||
      callerPayload?.user?.id;

    if (socket && appointmentId) {
      // Join room before emit so consultant receives room events too
      socket.emit("joinAppointmentRoom", { appointmentId });

      socket.emit("consultantBusy", {
        consultantId: consultantIdForJoin,
        userId: clientIdForJoin,
        isBusy: true,
      });

      socket.emit("consultantJoin", {
        joined: true,
        mode: appointment?.mode || callerData?.mode,
        data: callerPayload,
        appointmentId,
        clientId: clientIdForJoin,
        consultantId: consultantIdForJoin,
        userId: userDetails?._id,
      });
    }

    // Set joined flag and navigate to chat
    localStorage.setItem("joined", "true");
    sessionStorage.setItem("initiatedCallJoin", "true");
    if (appointmentId) {
      sessionStorage.removeItem(`prontopsy_rejoin_wait_${appointmentId}`);
      if (!localStorage.getItem(`actualConnectedSeconds_${appointmentId}`)) {
        localStorage.setItem(`actualConnectedSeconds_${appointmentId}`, "0");
      }
      try {
        localStorage.removeItem("actualConnectedSeconds");
      } catch (_) {}
      const callMode = appointment?.mode || callerData?.mode || "video";
      navigate(resolveConsultationPath(appointmentId, callMode));
    }
    // }

    setIsOpen(false);
  }, [
    appointment,
    callerData,
    callData,
    data,
    dispatch,
    navigate,
    sessionId,
    sessionToken,
    setIsOpen,
    socket,
    userDetails,
    audioRef,
    // hasStart,
  ]);

  const requestJoinNow = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const appointmentId = appointment?.appointmentId || callerData?.appointmentId;
    const mode = appointment?.mode || callerData?.mode || "video";
    if (String(mode).toLowerCase() === "chat") {
      handleJoinNow(e);
      return;
    }
    setCheckingReadiness(true);
    const ready = await hasUsableFreshDeviceReadiness(mode);
    if (ready) {
      setCheckingReadiness(false);
      saveCallDeviceReadiness(appointmentId, {
        audio: true,
        video: mode !== "voice",
      });
      handleJoinNow(e);
      return;
    }
    // Already marked ready for this mode: try a silent permission check so
    // the client is not left waiting on a second Test window.
    const profileReady =
      mode === "voice"
        ? userDetails?.readyForAudio === true
        : userDetails?.readyForVideo === true && userDetails?.readyForAudio === true;
    if (profileReady && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: mode !== "voice",
        });
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (_) {
            /* ignore */
          }
        });
        setCheckingReadiness(false);
        saveCallDeviceReadiness(appointmentId, {
          audio: true,
          video: mode !== "voice",
        });
        handleJoinNow(e);
        return;
      } catch (_) {
        /* show the manual test only if the silent check failed */
      }
    }
    setCheckingReadiness(false);
    stopRingtone("device-check", appointment?.appointmentId);
    setShowDeviceCheck(true);
  }, [appointment, callerData, handleJoinNow, userDetails?.readyForAudio, userDetails?.readyForVideo]);

  // Handle cancel call action
  const handleJoinLater = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    stopRingtone();
    stopAllCallMedia();
    stopAllRegisteredMedia();
    setIsOpen(false);

    // Cancel appointment in backend ONLY for instant / Call Now consultations
    const appointmentId = appointment?.appointmentId;
    if (appointmentId && isInstantAppointment(appointment)) {
      appointmentCancel({ appointmentId, closeReason: "no_show" });
    }

    // Emit socket events for cancellation
    if (socket) {
      socket.emit("consultantBusy", {
        consultantId: appointment?.callerData?.consultant?.id || "",
        userId: appointment?.callerData?.user?.id,
        isBusy: false,
      });

      socket.emit("vonageSessionReject", {
        disconnect: true,
        appointmentId: appointment?.appointmentId,
        role: userDetails?.roleCode,
        consultantId: appointment?.callerData?.consultant?.id || "",
        userId: appointment?.callerData?.user?.id,
      });
    }

    // Remove appointment data after a short delay
    setTimeout(() => {
      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
    }, 1000);
    // window.location.reload();
  }, [appointment, socket, userDetails, setIsOpen]);

  // Auto-reject after 1 minute of waiting to answer — pause while the
  // consultant is in a required device test so the test is not "no answer".
  useEffect(() => {
    if (!isOpen) {
      ringDeadlineRef.current = 0;
      ringPausedRemainingRef.current = null;
      return undefined;
    }
    if (!ringDeadlineRef.current) {
      ringDeadlineRef.current = Date.now() + 60000;
    }
    if (showDeviceCheck) {
      if (ringPausedRemainingRef.current == null) {
        ringPausedRemainingRef.current = Math.max(5000, ringDeadlineRef.current - Date.now());
      }
      return undefined;
    }
    if (ringPausedRemainingRef.current != null) {
      ringDeadlineRef.current = Date.now() + ringPausedRemainingRef.current;
      ringPausedRemainingRef.current = null;
    }
    const remaining = Math.max(250, ringDeadlineRef.current - Date.now());
    const timer = setTimeout(() => {
      if (socket) {
        socket.emit("autoDisconnectCall", {
          appointmentId: appointment?.appointmentId,
          callerData: appointment?.callerData,
        });
      }
      handleJoinLater();
    }, remaining);
    return () => clearTimeout(timer);
  }, [isOpen, showDeviceCheck, socket, appointment, handleJoinLater]);

  const prevShowDeviceCheckRef = useRef(false);
  useEffect(() => {
    const appointmentId = appointment?.appointmentId || callerData?.appointmentId;
    if (!socket || !appointmentId) return;

    if (showDeviceCheck && !prevShowDeviceCheckRef.current) {
      socket.emit("appointmentPing", { appointmentId, testingDevice: true });
    } else if (!showDeviceCheck && prevShowDeviceCheckRef.current) {
      socket.emit("appointmentPing", { appointmentId, testingDevice: false });
    }
    prevShowDeviceCheckRef.current = showDeviceCheck;
  }, [showDeviceCheck, socket, appointment?.appointmentId, callerData?.appointmentId]);

  // Reset toast flag when modal opens
  useEffect(() => {
    if (isOpen) {
      toastDisplayedRef.current = false;
    }
  }, [isOpen]);

  // Listen for session rejection events
  useSocket(
    "vonageSessionRejectResponse",
    async (data) => {
      if (!data?.disconnect) return;

      const isCurrentUserInvolved = userDetails?._id === data?.userId || userDetails?._id === data?.consultantId;

      if (!isCurrentUserInvolved) return;

      stopRingtone();
      stopAllCallMedia();
      stopAllRegisteredMedia();
      setIsOpen(false);

      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
    },
    [],
  );

  // Listen for consultant join response
  useSocket(
    "consultantJoinResponse",
    async (responseData) => {
      if (responseData?.joined === true && userDetails && responseData?.data?.consultant?.id === userDetails?._id) {
        setIsOpen(false);

        if (sessionStorage.getItem("initiatedCallJoin") === "true") {
          sessionStorage.removeItem("initiatedCallJoin");
          const appointmentId = appointment?.appointmentId || responseData?.appointmentId;
          const callMode = appointment?.mode || responseData?.data?.mode || "video";
          if (appointmentId) {
            navigate(resolveConsultationPath(appointmentId, callMode));
          }
        }
      }
    },
    [appointment, navigate, userDetails, setIsOpen],
  );

  // Format heading with dynamic mode
  // const formattedHeading = getLocalizedText("heading", {
  //   mode: callerData?.mode || "",
  // });

  const formattedHeading =
    appointment?.service === "direct-access-online-psychological-counseling"
      ? getLocalizedText("heading", {
        mode: appointment?.mode || "",
      })
      : getLocalizedText("scheduleHeading", {
        mode: callerData?.mode || "",
      });



  return (
    <>
      <PreCallDeviceCheck
        open={isOpen && showDeviceCheck}
        mode={appointment?.mode || callerData?.mode || "video"}
        onReady={({ hasCamera, hasMicrophone }) => {
          const appointmentId = appointment?.appointmentId || callerData?.appointmentId;
          saveCallDeviceReadiness(appointmentId, {
            audio: hasMicrophone,
            video: hasCamera,
          });
          setShowDeviceCheck(false);
          handleJoinNow();
        }}
        onCancel={() => {
          setShowDeviceCheck(false);
          handleJoinLater();
        }}
      />
      <Modal isOpen={isOpen && !showDeviceCheck} titleShow={false} size="xl2">
      <div className="flex flex-col items-center justify-center md:p-8 p-2 bg-white rounded-lg">
        {isIOS() && soundBlocked && (
          <button
            type="button"
            onClick={handleEnableSound}
            onTouchEnd={handleEnableSound}
            className="w-full mb-4 bg-amber-500 text-white font-bold py-3 px-4 rounded-xl shadow-lg animate-pulse text-sm md:text-base">
            {getLocalizedText("tapForSound")}
          </button>
        )}
        <img
          crossOrigin="anonymous"
          src={displayImage}
          alt={displayName || "Profile"}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = defaultUserImage;
          }}
          className="!w-32 !h-32 object-cover rounded-full border-4 border-gray-200 mb-6 flex items-center justify-center bg-gray-100"
        />

        <div className="text-lg font-medium text-gray-500 mb-6 capitalize text-center pb-4">
          <p className="text-prontopsy-pink text-2xl font-bold pb-0 uppercase">
            {displayName}
          </p>
          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(formattedHeading) }} />
        </div>

        <span className="mt-[-25px] text-center text-prontopsy-dodgerBlue">{getLocalizedText("subheading")}</span>

        <div className="flex md:space-x-6 space-x-4 mt-4 text-xl">
          <button
            onClick={requestJoinNow}
            disabled={checkingReadiness}
            className="bg-green-500 flex gap-4 items-center text-white md:px-8 px-4 py-4 rounded-lg shadow-md hover:bg-green-600 transition duration-300 ease-in-out md:text-lg text-base">
            <i className="fa-solid fa-phone"></i>
            {checkingReadiness ? "Joining…" : getLocalizedText("join")}
          </button>

          <button
            onClick={handleJoinLater}
            className="bg-red-500 flex md:gap-4 gap-2 items-center text-white md:px-8 px-4 py-4 rounded-lg shadow-md hover:bg-red-600 transition duration-300 ease-in-out md:text-lg text-base">
            <i className="fa-solid fa-xmark"></i>
            {getLocalizedText("cancel")}
          </button>
        </div>
      </div>
      </Modal>
    </>
  );
};
